<template>
    <section class="relative overflow-hidden border-b border-green-600 bg-gray-200 dark:bg-gray-800">
        <PagesPageHeader
            :breadcrumbs="breadcrumbs"
            title="Careers"
            description="We seek a motivated and ambitious people to join our team."
            icon="i-bi-person-plus"
            iconColor="green"
            color="blue"
            :image="true"
            src="/assets/images/sliders/slider-03.webp" />
    </section>

    <ULandingSection
        title="About Us"
        :description="`${config.public.company.name} is a leading agarwood plantation that provides expert guidance to our high-value, long-term partners. With a strong commitment to delivering exceptional service and results, we seek a motivated and ambitious people to join our team.`"
        :features="[
            {
                name: 'Job Title',
                description: 'Sales Advisor',
                icon: 'i-bi-person'
            },
            {
                name: 'Location',
                description: 'Thailand',
                icon: 'i-bi-pin-map'
            },
            {
                name: 'Position Type',
                description: 'Full-Time',
                icon: 'i-bi-award'
            }
        ]"
        align="right"
        :ui="{
            title: 'text-3xl font-bold text-green-600 sm:text-4xl lg:text-4xl'
        }">
        <NuxtImg
            format="webp"
            width="840"
            height="473"
            class="w-[100%] rounded-xl"
            src="/assets/images/gallery/gallery-09.webp"
            :alt="config.public.company.name" />
    </ULandingSection>

    <UDivider
        icon="i-ph-plant"
        :ui="{
            icon: {
                base: 'flex-shrink-0 w-10 h-10 text-green-600'
            }
        }" />

    <section class="bg-white py-10 dark:bg-gray-800">
        <UContainer>
            <div class="flex flex-col items-center justify-center md:grid md:grid-cols-1">
                <h2 class="mb-2 text-xl font-semibold text-dark-950">Position Overview:</h2>

                <p class="mb-6 text-base text-dark-600">
                    As a Sales Advisor at {{ config.public.company.name }}, you will be pivotal in helping our clients achieve their long-term
                    financial goals. You will be responsible for building and maintaining relationships with high-value investors, providing expert
                    financial advice, and ensuring their investment portfolios align with their financial objectives.
                </p>

                <h3 class="mb-2 text-lg font-semibold text-dark-950">Key Responsibilities:</h3>

                <ul class="mb-6 ml-6 list-disc space-y-2 text-base text-dark-600">
                    <li>
                        <span class="font-semibold">Client Relationship Management:</span> Cultivate and nurture strong relationships with high-value,
                        long-term investors. Understand their unique financial needs, goals, and risk tolerance
                    </li>

                    <li>
                        <span class="font-semibold">Financial Advisory:</span> Conduct comprehensive financial assessments and analysis to develop
                        tailored investment strategies for clients. Provide recommendations on asset allocation, portfolio diversification, and
                        investment opportunities.
                    </li>

                    <li>
                        <span class="font-semibold">Sales and Business Development:</span> Identify and pursue new business opportunities within the
                        high-value investor segment. Generate leads, pitch our services, and close sales deals to meet and exceed sales targets.
                    </li>

                    <li>
                        <span class="font-semibold">Market Research:</span> Stay up-to-date with market trends, economic developments, and investment
                        products. Share insights with clients to support informed decision-making.
                    </li>

                    <li>
                        <span class="font-semibold">Compliance:</span> Ensure adherence to all regulatory and compliance standards in the financial
                        advisory industry. Maintain accurate records and documentation.
                    </li>

                    <li>
                        <span class="font-semibold">Client Education:</span> Educate clients about investment products, risk factors, and potential
                        returns. Provide ongoing support and communication to address client inquiries and concerns.
                    </li>
                </ul>

                <h3 class="mb-2 text-lg font-semibold text-dark-950">Qualifications:</h3>

                <ul class="mb-6 ml-6 list-disc space-y-2 text-base text-dark-600">
                    <li>Proven track record of at least 3 years in financial advisory roles, focusing on high-value, long-term investors.</li>

                    <li>Strong understanding of investment strategies, financial markets, and investment products.</li>

                    <li>Excellent communication, negotiation, and interpersonal skills.</li>

                    <li>Self-motivated and goal-oriented, with a hunger for success and achievement.</li>

                    <li>Ability to work independently and as part of a collaborative team.</li>

                    <li>Proficiency in financial software and tools.</li>
                </ul>

                <h3 class="mb-2 text-lg font-semibold text-dark-950">Benefits:</h3>

                <ul class="mb-6 ml-6 list-disc space-y-2 text-base text-dark-600">
                    <li>Competitive base salary and performance-based bonuses</li>

                    <li>Ongoing training and professional development opportunities.</li>

                    <li>A supportive and dynamic work environment.</li>

                    <li>Opportunity for career advancement within the company.</li>
                </ul>

                <h3 class="mb-2 text-lg font-semibold text-dark-950">How to Apply:</h3>

                <p class="mb-2 text-base text-dark-600">
                    If you are an ambitious, hungry, and experienced Sales Advisor who is passionate about helping high-value, long-term investors
                    achieve financial success, please submit your resume and a cover letter detailing your relevant experience and why you are the
                    ideal candidate for this position. We look forward to hearing from you.
                </p>

                <p class="mb-2 text-base text-dark-600">
                    {{ config.public.company.name }} is an equal-opportunity employer and welcomes candidates of all backgrounds to apply. We
                    encourage diversity in our workplace.
                </p>
            </div>
        </UContainer>
    </section>
</template>

<script setup>
    definePageMeta({
        layout: 'default'
    })

    const config = useRuntimeConfig()
    const router = useRouter()

    useHead({
        title: `${config.public.company.name} | Agarwood Careers`,
        meta: [
            {
                name: 'description',
                content:
                    'With a strong commitment to delivering exceptional service and results, we seek a motivated and ambitious people to join our team.'
            },
            { name: 'robots', content: 'index, follow' }
        ],
        link: [{ rel: 'canonical', href: `${config.public.base.url + router.currentRoute.value.path}` }]
    })

    const breadcrumbs = [{ label: 'Home', to: '/' }, { label: 'Corporate', to: '/corporate' }, { label: 'Careers' }]
</script>
